import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { KnowledgeLibraryTab } from './KnowledgeLibraryTab';
import { KnowledgeUploadModal } from './KnowledgeUploadModal';
import { NoteEditor } from './NoteEditor';
import { SourceDetail } from './SourceDetail';

type DetailState =
  | { kind: 'edit-note'; contextId: string; readOnly?: boolean }
  | { kind: 'view-source'; contextId: string; readOnly?: boolean }
  | {
      kind: 'create';
      scope: 'user' | 'tenant';
      defaultApplyMode?: 'always' | 'chat';
    }
  | null;

export const KnowledgePage = ({ onClose }: { onClose: () => void }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [detail, setDetail] = useState<DetailState>(null);
  const [uploadScope, setUploadScope] = useState<'user' | 'tenant' | null>(
    null,
  );
  const [showLearnMore, setShowLearnMore] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showLearnMore) setShowLearnMore(false);
        else if (uploadScope) setUploadScope(null);
        else if (detail) setDetail(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, detail, uploadScope, showLearnMore]);

  const bumpRefresh = () => setRefreshKey(k => k + 1);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="knowledge-dialog-title"
      className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close Knowledge"
        onClick={onClose}
        className="absolute inset-0 bg-[#1a2226]/45 backdrop-blur-[2px] cursor-default"
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-4 sm:p-6 md:p-8">
        <div className="bg-[#F6F8F9] rounded-xl shadow-[0_24px_64px_rgba(16,24,40,.18)] flex flex-col w-full max-w-[900px] h-full max-h-[88vh] pointer-events-auto overflow-hidden border border-[#DDE4E8]">
          {!detail ? (
            <div className="flex-shrink-0 flex items-center justify-between gap-3 px-5 sm:px-7 py-3.5 border-b border-[#E4E9EC]/80 bg-gradient-to-r from-white via-white to-[#EEF4F7]">
              <div className="min-w-0">
                <p
                  id="knowledge-dialog-title"
                  className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#007CA0]">
                  Bernoulli
                </p>
                <p className="text-sm font-semibold text-[#252F33] truncate">
                  Knowledge library
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/90 text-[#768D95] border border-transparent hover:border-[#E4E9EC] transition-colors"
                aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
          ) : null}

          <div className="flex-1 overflow-y-auto px-6 sm:px-8 pb-8 pt-6">
            {detail?.kind === 'edit-note' ? (
              <NoteEditor
                contextId={detail.contextId}
                readOnly={detail.readOnly}
                onBack={() => setDetail(null)}
                onSaved={bumpRefresh}
                onDeleted={() => {
                  bumpRefresh();
                  setDetail(null);
                }}
              />
            ) : detail?.kind === 'view-source' ? (
              <SourceDetail
                contextId={detail.contextId}
                readOnly={detail.readOnly}
                onBack={() => setDetail(null)}
                onDeleted={() => {
                  bumpRefresh();
                  setDetail(null);
                }}
              />
            ) : detail?.kind === 'create' ? (
              <NoteEditor
                createScope={detail.scope}
                defaultApplyMode={detail.defaultApplyMode}
                onBack={() => setDetail(null)}
                onSaved={bumpRefresh}
              />
            ) : (
              <KnowledgeLibraryTab
                refreshKey={refreshKey}
                onLearnMore={() => setShowLearnMore(true)}
                onUploadSource={scope => setUploadScope(scope)}
                onOpenNote={(id, _scope, readOnly) =>
                  setDetail({ kind: 'edit-note', contextId: id, readOnly })
                }
                onOpenSource={(id, _scope, readOnly) =>
                  setDetail({ kind: 'view-source', contextId: id, readOnly })
                }
                onCreateNew={(scope, options) =>
                  setDetail({
                    kind: 'create',
                    scope,
                    defaultApplyMode: options?.defaultApplyMode,
                  })
                }
              />
            )}
          </div>
        </div>
      </div>

      {uploadScope && (
        <KnowledgeUploadModal
          scope={uploadScope}
          onClose={() => setUploadScope(null)}
          onUploaded={bumpRefresh}
        />
      )}

      {showLearnMore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setShowLearnMore(false)}
            className="absolute inset-0 bg-black/30"
          />
          <div className="relative w-full max-w-md rounded-lg bg-white shadow-2xl p-6">
            <h2 className="text-lg font-semibold text-[#252F33]">
              About knowledge
            </h2>
            <div className="mt-3 space-y-3 text-sm text-[#41525A] leading-relaxed">
              <p>
                <strong className="text-[#252F33]">
                  Sources are the evidence. Memory is the short summary
                  Bernoulli carries into every answer.
                </strong>
              </p>
              <p>
                Upload documents or write notes. Attach sources in chat when you
                need cited evidence. Set a note to &ldquo;Always
                considered&rdquo; to treat it as memory.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowLearnMore(false)}
              className="mt-5 text-sm text-[#007CA0] hover:underline font-medium">
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
